// Reusable history panel of every Tx design plan ever submitted for a
// case. Used at step 5 (Review design) as the main body, and at step 3
// (Submit Tx design) as a sidebar so the ortho can see past iterations
// while drafting a new one.

import { useQuery } from '@tanstack/react-query';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import { fetchTimeline } from '../../../services/timeline';
import type { TimelineEvent } from '../../patient-files/actions/types';
import {
    CARD_TEXT_CLASS,
    HEADER_BUTTON_CLASS,
    PANEL_BODY_CLASS,
    PanelHeader,
    VersionCard
} from './design-dx/VersionCard';

const STALE_MS = 60_000;

interface DesignPlanEntry {
    id: string;
    createdAt: string;
    author: string;
    designPlan: string;
    variant: 'submit' | 'pre-approved';
}

function extractEntry(
    event: TimelineEvent,
    variant: DesignPlanEntry['variant']
): DesignPlanEntry | null {
    const plan = (event.payload as { designPlan?: string } | undefined)?.designPlan;
    if (!plan) return null;
    return {
        id: event.id,
        createdAt: event.createdAt,
        author: event.createdByUser?.name ?? event.createdBy ?? 'Unknown',
        designPlan: plan,
        variant
    };
}

function versionsMeta(entries: DesignPlanEntry[] | undefined): string | null {
    return entries && entries.length > 1 ? `(${entries.length} versions)` : null;
}

interface PlanCardProps {
    entry: DesignPlanEntry;
    index: number; // 0 = latest
    total: number;
}

function PlanCard({ entry, index, total }: PlanCardProps) {
    const isLatest = index === 0;
    const versionLabel =
        total === 1 ? 'Submission' : isLatest ? `Latest · v${total - index}` : `v${total - index}`;
    const preApprovedBadge = entry.variant === 'pre-approved' && (
        <span className="inline-flex items-center px-2 py-[2px] rounded-md border border-emerald-200 bg-emerald-50 text-[10px] font-medium text-emerald-700">
            Pre-approved
        </span>
    );

    return (
        <VersionCard
            label={versionLabel}
            badge={preApprovedBadge}
            author={entry.author}
            at={entry.createdAt}
            muted={!isLatest}
        >
            <p className={`${CARD_TEXT_CLASS} whitespace-pre-wrap`}>{entry.designPlan}</p>
        </VersionCard>
    );
}

interface DesignPlanHistoryProps {
    patientId: string;
    /** Card title. Default: "Submitted Tx instructions". */
    title?: string;
    /** Empty-state copy. Default: "No instructions submitted yet for this case." */
    emptyText?: string;
    /**
     * Timeline event type(s) for normal submissions. Accepts a single string
     * (backward-compatible) or an array of types fetched in parallel and merged.
     * Default: "submit-design-plan".
     */
    submitType?: string | string[];
    /** Timeline event type for a pre-approved submission. Default: "design-pre-approved". */
    preApprovedType?: string;
    /**
     * Optional ISO cutoff — only submissions created at/after this instant are shown.
     * Used to scope the history to the current cycle (e.g. the case's phaseEnteredAt
     * so a refinement only shows designs from the current refinement, not prior ones).
     */
    since?: string | null;
    /** Drop the card border/radius so the panel sits directly on its host surface. */
    flush?: boolean;
    /** Rendered in the header, left of Refresh. */
    headerRight?: React.ReactNode;
    /** Rendered in the header, right of Refresh. */
    headerEnd?: React.ReactNode;
    /** Rendered at the top of the body, above the submissions. */
    bodyStart?: React.ReactNode;
}

const CARD_SHELL_CLASS = 'bg-white rounded-xl border border-gray-200';

export function DesignPlanHistory({
    patientId,
    title = 'Submitted Tx instructions',
    emptyText = 'No instructions submitted yet for this case.',
    submitType = 'submit-design-plan',
    preApprovedType = 'design-pre-approved',
    since = null,
    flush = false,
    headerRight,
    headerEnd,
    bodyStart
}: DesignPlanHistoryProps) {
    const submitTypes = Array.isArray(submitType) ? submitType : [submitType];

    const {
        data: entries,
        isLoading,
        isError,
        refetch,
        isFetching
    } = useQuery({
        queryKey: ['design-plan-history', patientId, submitTypes, preApprovedType, since],
        queryFn: async (): Promise<DesignPlanEntry[]> => {
            const submitFetches = submitTypes.map((t) =>
                fetchTimeline(patientId, { type: t, limit: 50 })
            );
            const [preApprovedRes, ...submitResults] = await Promise.all([
                fetchTimeline(patientId, { type: preApprovedType, limit: 50 }),
                ...submitFetches
            ]);
            const sinceMs = since ? new Date(since).getTime() : null;
            const merged: DesignPlanEntry[] = [
                ...submitResults.flatMap((res) => res.data.map((e) => extractEntry(e, 'submit'))),
                ...preApprovedRes.data.map((e) => extractEntry(e, 'pre-approved'))
            ]
                .filter((x): x is DesignPlanEntry => x !== null)
                .filter((x) => sinceMs === null || new Date(x.createdAt).getTime() >= sinceMs);
            return merged.sort((a, b) => b.createdAt.localeCompare(a.createdAt));
        },
        staleTime: STALE_MS,
        enabled: Boolean(patientId)
    });

    return (
        <div
            className={`flex-1 min-h-0 flex flex-col overflow-hidden font-inter ${flush ? '' : CARD_SHELL_CLASS}`}
        >
            <PanelHeader title={title} meta={versionsMeta(entries)}>
                {headerRight}
                <button
                    type="button"
                    onClick={() => refetch()}
                    disabled={isFetching}
                    className={HEADER_BUTTON_CLASS}
                    aria-label="Refresh"
                >
                    <ArrowPathIcon className={`w-3 h-3 ${isFetching ? 'animate-spin' : ''}`} />
                    Refresh
                </button>
                {headerEnd}
            </PanelHeader>
            <div className={PANEL_BODY_CLASS}>
                {bodyStart}
                {isLoading ? (
                    <div className="flex items-center justify-center h-full text-xs text-gray-400 italic">
                        Loading instructions…
                    </div>
                ) : isError ? (
                    <div className="flex items-center justify-center h-full text-xs text-red-500">
                        Failed to load instructions.
                    </div>
                ) : !entries || entries.length === 0 ? (
                    <div className="flex items-center justify-center h-full text-xs text-gray-400 italic text-center px-2">
                        {emptyText}
                    </div>
                ) : (
                    <div className="flex flex-col gap-3">
                        {entries.map((entry, i) => (
                            <PlanCard
                                key={entry.id}
                                entry={entry}
                                index={i}
                                total={entries.length}
                            />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
